import { useEffect, useState, type ReactNode } from 'react';
import { GitBranch, MapPin, Plus, Radio, Trash2, TriangleAlert } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/atoms/card';
import { Input } from '@/components/atoms/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/atoms/select';
import { cn } from '@/lib/utils';
import { LEAD_SOURCES, leadSourceLabel } from '@/lib/constants/lead-sources';
import { formatUserName } from '@/lib/leads/leadFormat';
import { useAssignableTeams, type AssignableTeam } from '@/hooks/leads/useAssignableTeams';
import { useLeadOwners } from '@/hooks/leads/useLeadOwners';
import { useUpdateLeadSettings } from '@/hooks/leads/useUpdateLeadSettings';
import type {
  LeadCategoryRoute,
  LeadSettings,
  LeadSourceRoute,
  LeadTerritoryRoute,
} from '@/hooks/leads/useLeadSettings';

// Red-border treatment for an empty required field.
const invalidFieldClass = 'border-destructive focus-visible:ring-destructive';

// ─── Destination picker (team OR user) ───────────────────────────────────────

const TEAM_PREFIX = 'team:';
const USER_PREFIX = 'user:';

interface RouteDestination {
  teamId?: string;
  userId?: string;
}

interface DestinationUser {
  id: string;
  firstName: string | null;
  lastName: string | null;
  displayName: string | null;
  email: string;
}

function destinationValue(dest: RouteDestination): string {
  if (dest.teamId) return `${TEAM_PREFIX}${dest.teamId}`;
  if (dest.userId) return `${USER_PREFIX}${dest.userId}`;
  return '';
}

function DestinationSelect({
  value,
  onChange,
  teams,
  users,
  disabled,
  invalid,
}: {
  value: RouteDestination;
  onChange: (next: RouteDestination) => void;
  teams: AssignableTeam[];
  users: DestinationUser[];
  disabled?: boolean;
  invalid?: boolean;
}) {
  return (
    <Select
      value={destinationValue(value)}
      disabled={disabled}
      onValueChange={(raw) => {
        if (raw.startsWith(TEAM_PREFIX)) {
          onChange({ teamId: raw.slice(TEAM_PREFIX.length) });
        } else if (raw.startsWith(USER_PREFIX)) {
          onChange({ userId: raw.slice(USER_PREFIX.length) });
        }
      }}
    >
      <SelectTrigger
        aria-invalid={invalid || undefined}
        className={cn('h-9', invalid && invalidFieldClass)}
      >
        <SelectValue placeholder="Route to…" />
      </SelectTrigger>
      <SelectContent>
        {teams.length > 0 ? <SelectGroupLabel label="Teams" /> : null}
        {teams.map((team) => (
          <SelectItem key={`team-${team.id}`} value={`${TEAM_PREFIX}${team.id}`}>
            {team.name}
          </SelectItem>
        ))}
        <SelectGroupLabel label="Users" />
        {users.map((user) => (
          <SelectItem key={`user-${user.id}`} value={`${USER_PREFIX}${user.id}`}>
            {formatUserName(user)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function SelectGroupLabel({ label }: { label: string }) {
  return <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">{label}</div>;
}

// ─── Generic rule row layout ─────────────────────────────────────────────────

function RuleRow({
  match,
  destination,
  onRemove,
  disabled,
}: {
  match: ReactNode;
  destination: ReactNode;
  onRemove: () => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex flex-wrap items-end gap-2 rounded-md border bg-muted/20 p-2.5 sm:flex-nowrap">
      <div className="min-w-0 flex-1">{match}</div>
      <span className="hidden pb-2 text-xs text-muted-foreground sm:block">→</span>
      <div className="min-w-0 flex-1">{destination}</div>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-9 w-9 shrink-0 text-muted-foreground hover:text-destructive"
        onClick={onRemove}
        disabled={disabled}
      >
        <Trash2 className="size-4" />
        <span className="sr-only">Remove rule</span>
      </Button>
    </div>
  );
}

function FieldLabel({ children }: { children: ReactNode }) {
  return <span className="mb-1 block text-xs text-muted-foreground">{children}</span>;
}

// Inline "this field is required" hint shown directly under an empty required
// input, so the user sees exactly which field (and which rule) is missing
// instead of a generic "fix N fields" toast.
function FieldError({ children }: { children: ReactNode }) {
  return (
    <span className="mt-1 flex items-center gap-1 text-xs text-destructive">
      <TriangleAlert className="size-3 shrink-0" />
      {children}
    </span>
  );
}

// ─── Section component ───────────────────────────────────────────────────────

interface LeadRoutingRulesSectionProps {
  settings: LeadSettings;
  canConfigure: boolean;
}

export function LeadRoutingRulesSection({ settings, canConfigure }: LeadRoutingRulesSectionProps) {
  const teamsQuery = useAssignableTeams({ enabled: true });
  const usersQuery = useLeadOwners({ enabled: true });
  const updateSettings = useUpdateLeadSettings();

  // Both endpoints already return active-only records, scoped to the leads
  // permission set — no client-side re-filtering needed.
  const teams = teamsQuery.data?.data ?? [];
  const users = usersQuery.data?.data ?? [];

  // Local drafts seeded from settings; persisted explicitly so we don't PUT per keystroke.
  const [categoryRoutes, setCategoryRoutes] = useState<LeadCategoryRoute[]>([]);
  const [territoryRoutes, setTerritoryRoutes] = useState<LeadTerritoryRoute[]>([]);
  const [sourceRoutes, setSourceRoutes] = useState<LeadSourceRoute[]>([]);

  useEffect(() => {
    setCategoryRoutes(settings.categoryRoutes ?? []);
    setTerritoryRoutes(settings.territoryRoutes ?? []);
    setSourceRoutes(settings.sourceRoutes ?? []);
  }, [settings.categoryRoutes, settings.territoryRoutes, settings.sourceRoutes]);

  const dirty =
    JSON.stringify(categoryRoutes) !== JSON.stringify(settings.categoryRoutes ?? []) ||
    JSON.stringify(territoryRoutes) !== JSON.stringify(settings.territoryRoutes ?? []) ||
    JSON.stringify(sourceRoutes) !== JSON.stringify(settings.sourceRoutes ?? []);

  function patchAt<T>(list: T[], index: number, patch: Partial<T>): T[] {
    return list.map((item, i) => (i === index ? { ...item, ...patch } : item));
  }

  // Replacing the destination must clear the other key (teamId XOR userId).
  function applyDestination<T extends RouteDestination>(item: T, dest: RouteDestination): T {
    return { ...item, teamId: undefined, userId: undefined, ...dest };
  }

  function handleSave() {
    updateSettings.mutate({ categoryRoutes, territoryRoutes, sourceRoutes });
  }

  const readOnly = !canConfigure;

  // Auto-assign is the master switch — routing rules only run while it is ON.
  // `inactive` reflects the SAVED value, which is the actual live behavior. When
  // off, the whole editor is read-only (UI-only — routing behavior is unchanged).
  const inactive = !settings.autoAssignEnabled;
  const editingDisabled = readOnly || inactive;

  // Frontend guard for #10: a drafted rule is incomplete until it has BOTH a
  // destination (team OR user) and a match key. The backend enforces the same.
  // Free-text match keys count as empty when they are whitespace-only, so a
  // rule like `category: '   '` can't slip past Save and persist as dead weight.
  const hasText = (value?: string | null) => Boolean(value?.trim());
  const incompleteRules =
    categoryRoutes.some(
      (r) => (!hasText(r.category) && !r.categoryId) || (!r.teamId && !r.userId),
    ) ||
    territoryRoutes.some(
      (r) => (!hasText(r.zip) && !hasText(r.zipPrefix)) || (!r.teamId && !r.userId),
    ) ||
    sourceRoutes.some((r) => !r.source || (!r.teamId && !r.userId));

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <span className="flex h-8 w-8 items-center justify-center rounded-md bg-muted">
            <GitBranch className="h-4 w-4 text-muted-foreground" />
          </span>
          Routing Rules
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Automatically send each new lead to the right team or person. When a lead arrives the
          engine checks these rules and sends it to the{' '}
          <span className="font-medium">first match</span>. Sending to a{' '}
          <span className="font-medium">team</span> spreads leads evenly across its available reps
          (round-robin); sending to a <span className="font-medium">person</span> assigns them
          directly. A lead that matches no rule goes to the{' '}
          <span className="font-medium">fallback user</span> set in Assignment above. These rules
          only run while <span className="font-medium">Auto-assign</span> is on.
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Auto-assign is the master switch; be honest when it's off (UI-only — routing
            behavior is unchanged). Banner shows regardless of edit permission. */}
        {inactive ? (
          <div
            role="alert"
            className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2.5 text-sm text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-200"
          >
            <TriangleAlert className="mt-0.5 size-4 shrink-0" />
            <p>
              <span className="font-medium">Auto-assign is off</span> — these routing rules won’t
              run until you turn on Auto-assign in Assignment above.
            </p>
          </div>
        ) : null}

        {/* ─── Category routes ─── */}
        <div className="space-y-2.5">
          <div className="flex items-center justify-between gap-2">
            <h4 className="flex items-center gap-1.5 text-sm font-medium">
              <GitBranch className="size-3.5 text-muted-foreground" />
              By service category
            </h4>
            {!readOnly ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={editingDisabled}
                onClick={() => setCategoryRoutes((prev) => [...prev, { category: '' }])}
              >
                <Plus className="size-4" />
                Add rule
              </Button>
            ) : null}
          </div>
          <p className="text-xs text-muted-foreground">
            Match what the customer needs — e.g. a “plumbing” lead → your Plumbing team. The keyword
            is matched against the lead’s requested service.
          </p>
          {categoryRoutes.length === 0 ? (
            <p className="text-xs text-muted-foreground">No category rules.</p>
          ) : (
            <div className="space-y-2">
              {categoryRoutes.map((route, index) => {
                const matchInvalid =
                  !editingDisabled && !hasText(route.category) && !route.categoryId;
                const destInvalid = !editingDisabled && !route.teamId && !route.userId;
                return (
                  <RuleRow
                    key={index}
                    disabled={editingDisabled}
                    onRemove={() => setCategoryRoutes((prev) => prev.filter((_, i) => i !== index))}
                    match={
                      <>
                        <FieldLabel>Category keyword</FieldLabel>
                        <Input
                          className={cn('h-9', matchInvalid && invalidFieldClass)}
                          aria-invalid={matchInvalid || undefined}
                          placeholder="e.g. plumbing"
                          value={route.category ?? ''}
                          disabled={editingDisabled}
                          onChange={(event) =>
                            setCategoryRoutes((prev) =>
                              patchAt(prev, index, { category: event.target.value }),
                            )
                          }
                        />
                        {matchInvalid ? <FieldError>Enter a keyword</FieldError> : null}
                      </>
                    }
                    destination={
                      <>
                        <FieldLabel>Destination</FieldLabel>
                        <DestinationSelect
                          value={route}
                          teams={teams}
                          users={users}
                          disabled={editingDisabled}
                          invalid={destInvalid}
                          onChange={(dest) =>
                            setCategoryRoutes((prev) => [
                              ...prev.slice(0, index),
                              applyDestination(prev[index], dest),
                              ...prev.slice(index + 1),
                            ])
                          }
                        />
                        {destInvalid ? <FieldError>Choose a destination</FieldError> : null}
                      </>
                    }
                  />
                );
              })}
            </div>
          )}
        </div>

        {/* ─── Territory routes ─── */}
        <div className="space-y-2.5">
          <div className="flex items-center justify-between gap-2">
            <h4 className="flex items-center gap-1.5 text-sm font-medium">
              <MapPin className="size-3.5 text-muted-foreground" />
              By territory (ZIP)
            </h4>
            {!readOnly ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={editingDisabled}
                onClick={() => setTerritoryRoutes((prev) => [...prev, { zip: '' }])}
              >
                <Plus className="size-4" />
                Add rule
              </Button>
            ) : null}
          </div>
          <p className="text-xs text-muted-foreground">
            Match the lead’s service-address ZIP — a full ZIP (90210) or a prefix (902) to cover a
            whole area. Great for assigning by region.
          </p>
          {territoryRoutes.length === 0 ? (
            <p className="text-xs text-muted-foreground">No territory rules.</p>
          ) : (
            <div className="space-y-2">
              {territoryRoutes.map((route, index) => {
                const matchInvalid =
                  !editingDisabled && !hasText(route.zip) && !hasText(route.zipPrefix);
                const destInvalid = !editingDisabled && !route.teamId && !route.userId;
                return (
                  <RuleRow
                    key={index}
                    disabled={editingDisabled}
                    onRemove={() =>
                      setTerritoryRoutes((prev) => prev.filter((_, i) => i !== index))
                    }
                    match={
                      <>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <FieldLabel>Exact ZIP</FieldLabel>
                            <Input
                              className={cn('h-9', matchInvalid && invalidFieldClass)}
                              aria-invalid={matchInvalid || undefined}
                              placeholder="90210"
                              value={route.zip ?? ''}
                              disabled={editingDisabled}
                              onChange={(event) =>
                                setTerritoryRoutes((prev) =>
                                  patchAt(prev, index, { zip: event.target.value }),
                                )
                              }
                            />
                          </div>
                          <div>
                            <FieldLabel>or ZIP prefix</FieldLabel>
                            <Input
                              className={cn('h-9', matchInvalid && invalidFieldClass)}
                              aria-invalid={matchInvalid || undefined}
                              placeholder="902"
                              value={route.zipPrefix ?? ''}
                              disabled={editingDisabled}
                              onChange={(event) =>
                                setTerritoryRoutes((prev) =>
                                  patchAt(prev, index, { zipPrefix: event.target.value }),
                                )
                              }
                            />
                          </div>
                        </div>
                        {matchInvalid ? <FieldError>Enter a ZIP or prefix</FieldError> : null}
                      </>
                    }
                    destination={
                      <>
                        <FieldLabel>Destination</FieldLabel>
                        <DestinationSelect
                          value={route}
                          teams={teams}
                          users={users}
                          disabled={editingDisabled}
                          invalid={destInvalid}
                          onChange={(dest) =>
                            setTerritoryRoutes((prev) => [
                              ...prev.slice(0, index),
                              applyDestination(prev[index], dest),
                              ...prev.slice(index + 1),
                            ])
                          }
                        />
                        {destInvalid ? <FieldError>Choose a destination</FieldError> : null}
                      </>
                    }
                  />
                );
              })}
            </div>
          )}
        </div>

        {/* ─── Source routes ─── */}
        <div className="space-y-2.5">
          <div className="flex items-center justify-between gap-2">
            <h4 className="flex items-center gap-1.5 text-sm font-medium">
              <Radio className="size-3.5 text-muted-foreground" />
              By source
            </h4>
            {!readOnly ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={editingDisabled}
                onClick={() => setSourceRoutes((prev) => [...prev, { source: '' }])}
              >
                <Plus className="size-4" />
                Add rule
              </Button>
            ) : null}
          </div>
          <p className="text-xs text-muted-foreground">
            Match where the lead came from — e.g. all Website or Angi leads → your inbound rep pool.
          </p>
          {sourceRoutes.length === 0 ? (
            <p className="text-xs text-muted-foreground">No source rules.</p>
          ) : (
            <div className="space-y-2">
              {sourceRoutes.map((route, index) => {
                const sourceInvalid = !editingDisabled && !route.source;
                const destInvalid = !editingDisabled && !route.teamId && !route.userId;
                return (
                  <RuleRow
                    key={index}
                    disabled={editingDisabled}
                    onRemove={() => setSourceRoutes((prev) => prev.filter((_, i) => i !== index))}
                    match={
                      <>
                        <FieldLabel>Source</FieldLabel>
                        <Select
                          value={route.source}
                          disabled={editingDisabled}
                          onValueChange={(value) =>
                            setSourceRoutes((prev) => patchAt(prev, index, { source: value }))
                          }
                        >
                          <SelectTrigger
                            aria-invalid={sourceInvalid || undefined}
                            className={cn('h-9', sourceInvalid && invalidFieldClass)}
                          >
                            <SelectValue placeholder="Select a source" />
                          </SelectTrigger>
                          <SelectContent>
                            {route.source &&
                              !LEAD_SOURCES.some((source) => source.value === route.source) && (
                                <SelectItem value={route.source}>
                                  {leadSourceLabel(route.source)}
                                </SelectItem>
                              )}
                            {LEAD_SOURCES.map((source) => (
                              <SelectItem key={source.value} value={source.value}>
                                {source.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {sourceInvalid ? <FieldError>Choose a source</FieldError> : null}
                      </>
                    }
                    destination={
                      <>
                        <FieldLabel>Destination</FieldLabel>
                        <DestinationSelect
                          value={route}
                          teams={teams}
                          users={users}
                          disabled={editingDisabled}
                          invalid={destInvalid}
                          onChange={(dest) =>
                            setSourceRoutes((prev) => [
                              ...prev.slice(0, index),
                              applyDestination(prev[index], dest),
                              ...prev.slice(index + 1),
                            ])
                          }
                        />
                        {destInvalid ? <FieldError>Choose a destination</FieldError> : null}
                      </>
                    }
                  />
                );
              })}
            </div>
          )}
        </div>

        {!readOnly ? (
          <div className="flex flex-wrap items-center justify-end gap-3 border-t pt-4">
            {incompleteRules ? (
              <p className="text-xs text-muted-foreground">
                Every rule needs a match keyword and a destination before you can save.
              </p>
            ) : null}
            <Button
              type="button"
              onClick={handleSave}
              disabled={!dirty || incompleteRules || updateSettings.isPending || editingDisabled}
            >
              {updateSettings.isPending ? 'Saving…' : 'Save routing rules'}
            </Button>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
